import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography, chat } from '../../theme';
import { apiService } from '../../services/api';
import { useLocale } from '../../contexts/LocaleContext';
import { createTranslator } from '../../i18n';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface DataSetReplyCardProps {
  content?: string;
  quickReplyItemId?: string;
  isOutgoing?: boolean;
}

export const DataSetReplyCard: React.FC<DataSetReplyCardProps> = ({
  content,
  quickReplyItemId,
  isOutgoing = false,
}) => {
  const styles = useThemedStyles(getStyles);
  const { locale } = useLocale();
  const t = createTranslator(locale);
  const cardStyles = isOutgoing ? styles.cardOutgoing : styles.cardIncoming;
  const textStyles = isOutgoing ? styles.textOutgoing : styles.textIncoming;
  // Don't show raw button IDs (item_xxx) as content
  const contentAsDisplay =
    content && typeof content === 'string' && content.trim() && !content.trim().startsWith('item_')
      ? content.trim()
      : null;

  const [fetchedLabel, setFetchedLabel] = useState<string | null>(null);
  const [loadingLabel, setLoadingLabel] = useState(false);

  // Inbound: fetch display text by quickReplyItemId when we only have item_id
  useEffect(() => {
    if (isOutgoing || contentAsDisplay) return;
    if (!quickReplyItemId) {
      setFetchedLabel(null);
      return;
    }
    let cancelled = false;
    setLoadingLabel(true);
    apiService
      .getQuickReplyItemById(quickReplyItemId)
      .then((res) => {
        if (!cancelled && res?.data) {
          const label =
            res.data.displayText || res.data.title || (res.data.config as any)?.displayText || null;
          setFetchedLabel(label || null);
        } else if (!cancelled) setFetchedLabel(null);
      })
      .catch(() => {
        if (!cancelled) setFetchedLabel(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingLabel(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOutgoing, quickReplyItemId, contentAsDisplay]);

  const displayContent = contentAsDisplay || fetchedLabel;

  return (
    <View style={[styles.card, cardStyles]}>
      <View style={styles.header}>
        <Ionicons name="list-outline" size={18} color={isOutgoing ? chat.out.accent : colors.primary} />
        <Text style={[styles.badge, textStyles]}>{t('card.quickReply')}</Text>
      </View>
      {loadingLabel ? (
        <ActivityIndicator size="small" color={isOutgoing ? chat.out.accent : colors.primary} style={styles.loader} />
      ) : displayContent ? (
        <Text style={[styles.content, textStyles]}>{displayContent}</Text>
      ) : (
        <Text style={[styles.content, textStyles, styles.placeholder]}>{t('card.selectedOption')}</Text>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    borderRadius: 20,
    padding: 14,
    maxWidth: '92%',
  },
  loader: {
    marginVertical: spacing.xs,
  },
  cardOutgoing: {
    backgroundColor: chat.out.bg,
  },
  cardIncoming: {
    backgroundColor: colors.background.secondary,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  badge: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    opacity: 0.95,
  },
  content: {
    fontSize: typography.fontSize.sm,
    opacity: 0.95,
  },
  placeholder: {
    fontStyle: 'italic',
    opacity: 0.8,
  },
  textOutgoing: {
    color: chat.out.text,
  },
  textIncoming: {
    color: colors.text.primary,
  },
});
